import { useEffect, useMemo, useRef } from 'react';
import { useInViewport } from 'ahooks';
import { ProductTracker, ThirdPartyTracker } from '@/utils/Tracker';
import { YELLOW_AVATAR_LIST, AVATAR } from '@/const/customerService';
import { MobxStore } from '@/mobx/types';
import styles from '../index.module.less';

const TEXT_COLOR = '#191C21';
const LINK_COLOR = '#31A5FF';

// 仅在以下产品页展示客服入口，并且替换词条中的产品名
const customerServiceProductMapper = new Map([
  [13, ' ONE X2'],
  [19, 'X3'],
  [24, 'GO 3'],
  [25, 'Ace Pro & Ace'],
  [26, 'X4'],
  [27, 'GO 3S'],
  [33, 'X5'],
]);

/** X4产品页客服入口ABTest
 * [PROD]: https://arashivision.feishu.cn/docx/Ju4mdVa0FoGH9px5e4cc9QOBnpd
 */
const ABTEST_FlAG_NAME = 'X4_customer_entry_copy';
const VARIATION_CONTENT: Record<X_Language, { title: string; subTitle: string }> = {
  'en_us': {
    title: 'Need help choosing a bundle?',
    subTitle: 'Chat with our experts now',
  },
  'de_de': {
    title: 'Brauchst du Hilfe bei der Auswahl eines Bundles?',
    subTitle: 'Chatte jetzt mit unseren Experten.',
  },
  'es_es': {
    title: '¿Necesitas ayuda para elegir un paquete?',
    subTitle: 'Habla con nuestros expertos ahora.',
  },
  'fr_fr': {
    title: "Vous avez besoin d'aide pour choisir un pack ?",
    subTitle: 'Discutez avec nos experts maintenant.',
  },
  'it_it': {
    title: 'Hai bisogno di aiuto per scegliere un Pack?',
    subTitle: 'Chatta con i nostri esperti adesso.',
  },
  'ja_jp': {
    title: 'キット選びにお困りですか？',
    subTitle: '今すぐ担当者にチャットする',
  },
  'ko_kr': {
    title: '어떤 번들을 선택해야 할지 고민 중이신가요?',
    subTitle: '저희의 전문가에게 문의해 보세요!',
  },
  'zh_cn': {
    title: '需要协助选购套餐？',
    subTitle: '欢迎咨询我们的专家',
  },
  'zh_tw': {
    title: '需要協助選購套餐？',
    subTitle: '歡迎諮詢我們的專家',
  },
};

interface IProps {
  area: MobxStore['area'];
  showUDesk: boolean;
  pid: number;
  abtest: MobxStore['abtest'];
}

/**
 * 产品页营销模块新增客服咨询入口
 * [PROD]: https://arashivision.feishu.cn/wiki/LiIhwVn6JiTBKfkwWGVcO7Z2n2c
 */
const useCustomerService = ({ area, showUDesk, pid, abtest }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(containerRef);
  const hasExposed = useRef(false);
  const {
    LANG,
    country: { code: countryCode, X_Language },
  } = area;
  const { isTargetingResult, isExpOn } = abtest;

  // 获取当前地区客服头像
  const avtarURL = useMemo(
    () => (YELLOW_AVATAR_LIST.includes(countryCode) ? AVATAR.YELLOW : AVATAR.WHITE),
    [countryCode]
  );

  const content = useMemo(() => {
    const productName = customerServiceProductMapper.get(pid);
    if (!productName) return null;

    if (isTargetingResult(ABTEST_FlAG_NAME, 'change_copy'))
      return {
        productName,
        ...(VARIATION_CONTENT[X_Language] || VARIATION_CONTENT['en_us']),
      };

    return {
      productName,
      title: LANG['productNew.customer_service_title']?.replace('X4', productName),
      subTitle: LANG['productNew.customer_service_subtitle']?.replace('X4', productName),
    };
  }, [pid]);

  // 加载Udesk
  const handleInitUdesk = () => window.loadUdesk?.();

  const handleClick = () => {
    ProductTracker.productPageCustomerService({
      action: 'click_online_service',
      label: content?.productName || '',
    });

    if (showUDesk) {
      window.showUdPanel?.();
    } else {
      window?.Intercom?.('showNewMessage');
    }

    if (pid === 26 && isExpOn(ABTEST_FlAG_NAME)) ThirdPartyTracker.ABTastyTracker.sendEvent({ KPI: 'click_x4_entry' });
  };

  // 模块首次曝光埋点上报
  useEffect(() => {
    if (inViewport && !hasExposed.current) {
      ProductTracker.productPageCustomerService({
        action: 'expose_online_service',
        label: content?.productName || '',
      });
      hasExposed.current = true;
    }
  }, [inViewport, content]);

  if (!content) return null;

  return {
    icon: avtarURL,
    html: (
      <div
        id='customer_service'
        ref={containerRef}
        onMouseEnter={handleInitUdesk}
        style={{
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '18px',
          color: TEXT_COLOR,
        }}
      >
        <div>{content.title}</div>
        <div
          onClick={handleClick}
          className={styles.customerService}
          style={{
            marginTop: '3px',
            color: LINK_COLOR,
            cursor: 'pointer',
          }}
        >
          {content.subTitle}
          <svg
            style={{ marginLeft: '6px' }}
            width='6'
            height='10'
            viewBox='0 0 6 10'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path d='M1 1L5 5L1 9' stroke='#31A5FF' strokeWidth='1.2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </div>
      </div>
    ),
  };
};

export default useCustomerService;
